import { push } from 'connected-react-router';
import i18njs from 'i18n-js';

import toastr from './toastr';
import { store } from '../redux/store';
import { sendError } from '../helpers/errorReport';

import { TokenType, RequestArguments } from '../types/helpers/requests';

const DEFAULT_RETRIES = parseInt(process.env.REACT_APP_REQUEST_RETRIES || '5');

const handleErrorString = (error: any, skipToast: boolean) => {
  let errObj;
  try {
    // Check if response is stringified object
    errObj = JSON.parse(error);
  } catch (er) {
    const _err = {
      message: error,
    };
    !skipToast && toastr.error(error);
    throw _err;
  }
  if (errObj.validation?.body?.message) {
    !skipToast && toastr.error(errObj.validation.body.message);
    const _err = {
      code: errObj?.status || errObj?.code,
      message: errObj.validation.body.message,
    };
    throw _err;
  }
  if (errObj.message) {
    if (errObj.message.indexOf('celebrate request validation failed') > -1 && errObj.validation?.body?.message) {
      !skipToast && toastr.error(errObj.validation.body.message);
      const _err = {
        code: errObj.status || errObj.code,
        message: errObj.validation.body.message,
      };
      throw _err;
    }
    !skipToast && toastr.error(errObj.message);
    const _err = {
      code: errObj?.status || errObj?.code,
      message: errObj.message,
    };
    throw _err;
  } else if (errObj.errors) {
    const singleError = errObj.errors.join('\n');
    !skipToast && toastr.error(singleError);
    const _err = {
      code: errObj?.status || errObj?.code,
      message: singleError,
    };
    throw _err;
  }
  const _err = {
    code: errObj?.status || errObj?.code,
    message: JSON.stringify(errObj),
  };
  !skipToast && toastr.error(_err.message);
  throw _err;
};

const errorHandler = async (error: any, skipToast: boolean) => {
  if (typeof error === 'string') {
    return handleErrorString(error, skipToast);
  }
  if (error.message === 'Failed to fetch') {
    if (navigator.onLine) {
      !skipToast && toastr.error('Server unreachable');
      const _err = {
        code: 400,
        message: 'Server unreachable',
      };
      sendError(error, {
        type: 'SERVER_UNREACHABLE',
      });
      throw _err;
    }
    !skipToast && toastr.error('No internet connection');
    const _err = {
      code: 400,
      message: 'No internet connection',
    };
    throw _err;
  }
  !skipToast && toastr.error(error.message);
  const _err = {
    code: error.status || error.code,
    message: `Unrecognised error, please contact support. ${error.message}`,
  };
  throw _err;
};

const constructHeaders = (additionalHeaders = {} as Record<string, string>, token: TokenType) => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };

  if (typeof token !== 'undefined') {
    headers.Authorization = `JWT ${token}`;
  }

  return headers;
};

const constructUrl = (endpoint: string, params = {} as Record<string, string>): URL => {
  const url = new URL(`${process.env.REACT_APP_API_URL}${endpoint}`);

  if (params) {
    Object.keys(params).forEach((key) => {
      if (!params[key]) return;
      else url.searchParams.append(key, params[key]);
    });
  }

  return url;
};

const handleSuccessfulResponse = (res: Response): string | object => {
  const contentType = res.headers.get('content-type');
  if (contentType && contentType.indexOf('application/json') > -1) {
    return res.json();
  }
  return res.text();
};

const logoutUser = (): void => {
  store.dispatch({
    type: 'LOGOUT_REQUEST',
    payload: {
      data: {
        dontCheckForUnauthorized: true,
      },
    },
  });
  store.dispatch(push('/login'));
};

const validateToken = (token: TokenType): token is string => {
  return typeof token === 'string' && token.length > 0;
};

const unauthorizedHandler = async (res: Response, requestArguments: RequestArguments) => {
  const { retries = DEFAULT_RETRIES, saml, skipToast } = requestArguments;

  if (saml === true) {
    store.dispatch({
      type: 'SAML_LOGOUT',
    });
    store.dispatch(push('/saml'));
    const responseErrorToText = await res.text();
    return errorHandler(responseErrorToText, !!skipToast);
  }
  if (retries >= 0) {
    const refreshToken = store.getState().user.refreshToken;
    const newToken = refreshToken ? await tryToRetrieveNewToken(refreshToken) : null;

    if (validateToken(newToken)) {
      store.dispatch({
        type: 'UPDATE_ACCESS_TOKEN',
        data: {
          token: newToken,
        },
      });
      store.dispatch({
        type: 'GET_USER_REQUEST',
      });
      return send({ ...requestArguments, token: newToken, retries: retries - 1 });
    }
  }

  logoutUser();
  const responseErrorToText = await res.text();
  return errorHandler(responseErrorToText, !!skipToast);
};

const handleResponse = async (res: Response, requestArguments: RequestArguments) => {
  const { dontCheckForUnauthorized, skipToast } = requestArguments;

  // request succeeded
  if (res.ok) {
    return handleSuccessfulResponse(res);
  }

  // request failed
  const shouldHandleUnauthorized = !dontCheckForUnauthorized && res.status === 401;
  if (shouldHandleUnauthorized) {
    return unauthorizedHandler(res, requestArguments);
  }

  if (res.status >= 500) {
    return errorHandler(i18njs.t('errors.messages.generic_request_error'), !!skipToast);
  }

  const responseErrorToText = await res.text();
  return errorHandler(responseErrorToText, !!skipToast);
};

const processChunkedResponse = (response: Response, skipToast: boolean) => {
  let text = '';
  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  function appendChunks(result: ReadableStreamDefaultReadResult<Uint8Array>): string | Promise<any> | undefined {
    const chunk = decoder.decode(result?.value || new Uint8Array(), { stream: !result.done });
    text += chunk;
    if (result.done) {
      return text;
    } else {
      return readChunk();
    }
  }

  function readChunk() {
    return reader
      ?.read()
      .then(appendChunks)
      .catch((e) => {
        errorHandler(e, skipToast);
      });
  }

  return reader ? readChunk() : null;
};

const handleStreamedResponse = async (res: Response, requestArguments: RequestArguments) => {
  const { dontCheckForUnauthorized, skipToast } = requestArguments;

  // request succeeded
  if (res.ok) {
    let result = await processChunkedResponse(res, !!skipToast);
    try {
      result = JSON.parse(result);
    } catch (e) {
      // ignore
    }
    return result;
  }

  // request failed
  const shouldHandleUnauthorized = !dontCheckForUnauthorized && res.status === 401;
  if (shouldHandleUnauthorized) {
    return unauthorizedHandler(res, requestArguments);
  }

  if (res.status >= 500) {
    return errorHandler(i18njs.t('errors.messages.generic_request_error'), !!skipToast);
  }

  const responseErrorToText = await res.text();
  return errorHandler(responseErrorToText, !!skipToast);
};

const send = async (requestArguments: RequestArguments): Promise<any> => {
  const {
    method,
    url,
    data,
    params,
    token = null,
    headers,
    credentials = 'include',
    skipToast,
    cache = 'no-cache',
    useStream,
  } = requestArguments;
  try {
    const finalHeaders = constructHeaders(headers, token);
    const fullUrl = constructUrl(url, params);

    const res = await fetch(fullUrl.toString(), {
      body: JSON.stringify(data),
      cache,
      headers: finalHeaders,
      method,
      credentials,
    });

    if (useStream) {
      return handleStreamedResponse(res, requestArguments);
    } else {
      return handleResponse(res, requestArguments);
    }
  } catch (error) {
    return errorHandler(error, !!skipToast);
  }
};

const tryToRetrieveNewToken = async (refreshToken: TokenType): Promise<string | null> => {
  try {
    const newAccessToken = await send({
      method: 'POST',
      url: '/pub/token',
      data: {
        refreshToken,
      },
    });
    return newAccessToken.token;
  } catch (error) {
    console.error(error);
    return null; // send() will handle the absence of new token
  }
};

export { send };
